import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Round 29 §2.3: the debiteur on the werkbon, and where it came from. An empty
// field reads as "fine"; a werkbon without a debiteur can never be invoiced, so
// it says so in words.
const AUTO = "__auto__";

export const WerkbonDebiteur = ({ bon, canWrite, onSaved }) => {
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/entities").then(({ data }) => setEntities(data)).catch(() => {});
  }, []);

  const bron = bon.billed_to_entity_bron;
  // m4 / M1(c): a dangling entity id and your own BV are BOTH "no debiteur" on
  // the money screen, so the card may not show them as a normal debiteur.
  const dangling = !!bon.billed_to_entity && !bon.debiteur_naam;
  const eigenBv = !!bon.debiteur_is_eigen_bv;
  const heeftDebiteur = !!bon.billed_to_entity && !dangling && !eigenBv;
  const herkomst =
    bron === "asset" && bon.debiteur_asset_code
      ? `overgenomen van asset ${bon.debiteur_asset_code}`
      : bron === "locatie" && bon.debiteur_locatie_naam
        ? `overgenomen van locatie ${bon.debiteur_locatie_naam}`
        : bron === "handmatig"
          ? `handmatig gekozen${bon.billed_to_entity_by_name ? ` door ${bon.billed_to_entity_by_name}` : ""}`
          : null;

  const save = async (value) => {
    setBusy(true);
    try {
      await api.put(`/werkbonnen/${bon.id}`, {
        billed_to_entity: value === AUTO ? null : value,
      });
      toast.success("Debiteur bijgewerkt");
      await onSaved();
    } catch (e) {
      toast.error(getErrorMessage(e, "Debiteur wijzigen mislukt."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="werkbon-debiteur">
      <div className="text-body font-semibold text-foreground">Debiteur</div>
      {heeftDebiteur ? (
        <div className="mt-1 text-body text-foreground" data-testid="werkbon-debiteur-naam">
          {bon.debiteur_naam}
        </div>
      ) : eigenBv ? (
        <div className="mt-1 text-body text-letop-tekst" data-testid="werkbon-debiteur-eigen-bv">
          Dit is je eigen BV — kies de debiteur
        </div>
      ) : (
        <div className="mt-1 text-body text-letop-tekst" data-testid="werkbon-debiteur-geen">
          Geen debiteur — deze werkbon kan niet op een factuur
        </div>
      )}
      {herkomst && heeftDebiteur && (
        <p className="mt-0.5 text-data text-muted-foreground" data-testid="werkbon-debiteur-herkomst">{herkomst}</p>
      )}
      {bon.factuur_nummer ? (
        <p className="mt-2 text-data text-muted-foreground" data-testid="werkbon-debiteur-bevroren">
          Deze werkbon staat op factuur {bon.factuur_nummer}; de debiteur staat vast.
        </p>
      ) : canWrite ? (
        <div className="mt-3 max-w-sm space-y-1.5">
          <Label>Debiteur wijzigen</Label>
          <Select value={heeftDebiteur ? bon.billed_to_entity : AUTO} onValueChange={save} disabled={busy}>
            <SelectTrigger data-testid="werkbon-debiteur-select">
              <SelectValue placeholder="Kies een debiteur" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO}>Automatisch (van asset of locatie)</SelectItem>
              {entities.map((e) => (
                <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </Card>
  );
};
